import { Box, CircularProgress, Popper } from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  PostAddOutlined as FreeIcon,
  VerifiedOutlined as VerifiedIcon,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { v2 } from "../ui/v2Tokens";
// The catalog, not the legacy `foods` table: menus resolve their food from food_dictionary, so a
// picker still reading the old table would offer foods a menu cannot reference.
//
// FoodDictionaryService rather than FoodService, though both now call the same catalog endpoint:
// this is the one the rest of the app searches through, and its `toBuilderShape` already returns
// `availableServingSizes` / `nutritionValues` — exactly what CatalogFood describes and what this
// picker reads. Two services for one search is one too many; this is the one that stays.
import { searchFoods } from "../../../../services/FoodDictionaryService";
import type { CatalogFood } from "../../../../services/catalogFood";
import {
  foodToItem,
  pickServing,
  readFiber,
  toServingOptions,
  type FoodItemV2,
  type MacroSet,
} from "../model/nutritionV2Model";
import { MacroLine } from "../ui/MacroChips";
import { TextButton } from "../ui/primitives";
import CustomFoodDialog from "../dialogs/CustomFoodDialog";

const DEBOUNCE_MS = 250;
/** How many foods to show on focus, before anything is typed, just so the field isn't blank. */
const PREVIEW_SIZE = 5;

/**
 * A search hit.
 *
 * There used to be two kinds — a system food and one the company created — searched from two
 * endpoints and merged here. There is one now: a company food lives in the same catalog with a
 * `company_id` on it, and the search returns both in a single ranked query. All that survives of
 * the distinction is `scope`, which decides the badge.
 */
type Candidate = {
  key: string;
  name: string;
  brand?: string;
  isCompanyOwn: boolean;
  /** the figures were checked against a real label — `certified` on the catalog food */
  verified: boolean;
  food: CatalogFood;
};

const toCandidate = (food: CatalogFood): Candidate => ({
  key: `food-${food.id}`,
  name: food.name || "",
  brand: food.brand,
  isCompanyOwn: food.scope === "COMPANY",
  verified: Boolean(food.certified),
  food,
});

/**
 * What to preview in the dropdown row: the SERVING that picking this food would add, which is the
 * one `foodToItem` chooses.
 *
 * It used to read the food's own `nutritionValues`. Those are the four macros and nothing else —
 * no fiber — so every result read "0 גר׳ סיבים" even for a food the catalogue has fiber for. The
 * serving option carries it, because the dictionary records fiber as a micronutrient and the
 * server scales it per serving.
 */
const candidatePreview = (candidate: Candidate): MacroSet => {
  const serving = pickServing(toServingOptions(candidate.food));
  if (serving?.nutrition) return serving.nutrition;

  const n = candidate.food.nutritionValues || {};
  return {
    calories: n.calories || 0,
    protein: n.protein || 0,
    carbs: n.carbs || 0,
    fats: n.fats || 0,
    fiber: readFiber(n),
  };
};

type Props = {
  onAdd: (item: FoodItemV2) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** used by parents to focus this field programmatically */
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
};

/**
 * The ONLY way to add a food in V2 — an inline search field, no picker dialog.
 */
const FoodSearchField = ({ onAdd, placeholder = "חפש מוצר מזון...", autoFocus, inputRef }: Props) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<Candidate[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  // true while the dropdown is showing the "here is what exists" list rather than
  // results for something the trainer typed
  const [isPreview, setIsPreview] = useState(false);
  // opening the preview is driven by focus, but `add()` refocuses the field on purpose —
  // reopening the dropdown right after adding would cover the row that was just added
  const skipFocusPreview = useRef(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // the SEARCH FIELD, not the whole row — the results must not run under the
  // quantity / unit controls
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const input = inputRef || localInputRef;

  useEffect(() => {
    const term = keyword.trim();
    if (!term) {
      setResults([]);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      // ONE request. The catalog returns the company's own foods alongside the shared ones,
      // already ranked together, so there is no second round trip to wait on at typing speed and
      // no client-side merge that could order the two lists differently from how the server ranks
      // within either.
      const res = await searchFoods({ keyword: term, size: 12 });
      if (cancelled) return;
      const content: CatalogFood[] = res?.data?.content || [];
      const found = content.map(toCandidate);
      setResults(found);
      setOpen(found.length > 0);
      setIsPreview(false);
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLoading(false);
    };
  }, [keyword]);

  // close the dropdown when clicking elsewhere
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inWrap = wrapRef.current?.contains(target);
      const inPopper = popperRef.current?.contains(target);
      if (!inWrap && !inPopper) setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  /**
   * Picking a result adds it straight away, at one default serving. Quantity and unit are
   * edited on the row itself afterwards — staging them here meant three controls and a
   * confirm button before anything appeared in the meal.
   */
  const choose = (candidate: Candidate) => {
    const serving = pickServing(toServingOptions(candidate.food));
    onAdd(foodToItem(candidate.food, { servingSizeId: serving?.id, quantity: 1 }));
    reset();
    // the field keeps focus for the next food, but without reopening the dropdown over
    // the row that was just added
    skipFocusPreview.current = true;
    input.current?.focus();
  };

  /**
   * Clicking into an empty field shows the first few foods, so the trainer can see what
   * exists instead of guessing a keyword. Typing replaces this with a real search.
   */
  const showPreview = async () => {
    setLoading(true);
    const res = await searchFoods({ size: PREVIEW_SIZE });
    const content: CatalogFood[] = res?.data?.content || [];
    const preview = content.map(toCandidate);
    setResults(preview);
    setIsPreview(true);
    setOpen(preview.length > 0);
    setLoading(false);
  };

  const onFocus = () => {
    if (skipFocusPreview.current) {
      skipFocusPreview.current = false;
      return;
    }
    if (results.length > 0) {
      setOpen(true);
      return;
    }
    if (!keyword.trim()) showPreview();
  };

  const reset = () => {
    setKeyword("");
    setResults([]);
    setIsPreview(false);
    setOpen(false);
  };

  return (
    <Box
      ref={wrapRef}
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "14px",
        flexWrap: "wrap",
        padding: "10px 12px",
        borderTop: `1px dashed ${v2.line}`,
        background: v2.canvas,
      }}
    >
      <Box ref={fieldRef} sx={{ position: "relative", flex: "1 1 200px", minWidth: "160px", marginInlineEnd: "6px" }}>
        <Box
          component="input"
          ref={input}
          type="text"
          autoFocus={autoFocus}
          autoComplete="off"
          placeholder={placeholder}
          aria-label={placeholder}
          value={keyword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
          onFocus={onFocus}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Escape") reset();
          }}
          sx={{
            width: "100%",
            boxSizing: "border-box",
            height: "36px",
            paddingInlineStart: "32px",
            paddingInlineEnd: "10px",
            borderRadius: v2.rSm,
            border: `1.5px solid ${v2.line}`,
            background: v2.surface,
            fontSize: "13px",
            fontFamily: "inherit",
            color: v2.ink,
            outline: "none",
            "&:focus": { borderColor: v2.brand },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            insetInlineStart: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "13px",
            color: v2.ink3,
            pointerEvents: "none",
          }}
        >
          {loading ? <CircularProgress size={12} sx={{ color: v2.ink3 }} /> : <SearchIcon sx={{ fontSize: 15 }} />}
        </Box>

        {/*
          The results render in a Popper (a portal) rather than inside this wrapper:
          every ancestor card uses `overflow: hidden` for its rounded corners, which
          would CLIP the dropdown no matter how high its z-index is — most visibly
          inside a substitution group, which is two cards deep.
        */}
        <Popper
          open={open}
          anchorEl={fieldRef.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: fieldRef.current?.offsetWidth, maxWidth: 420 }}
        >
          <Box
            ref={popperRef}
            sx={{
              marginTop: "4px",
              maxHeight: "230px",
              overflowY: "auto",
              background: v2.surface,
              border: `1.5px solid ${v2.line}`,
              borderRadius: v2.rSm,
              boxShadow: v2.shadowMenu,
            }}
          >
            {isPreview && (
              <Box
                sx={{
                  padding: "6px 10px",
                  fontSize: "10.5px",
                  fontWeight: 700,
                  color: v2.ink3,
                  background: v2.canvas,
                  borderBottom: `1px solid ${v2.lineSoft}`,
                }}
              >
                מזונות מהמאגר — הקלד כדי לחפש
              </Box>
            )}
            {results.map((candidate) => (
              <Box
                key={candidate.key}
                component="button"
                type="button"
                data-testid="food-search-option"
                onClick={() => choose(candidate)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "start",
                  padding: "6px 10px",
                  border: "none",
                  borderBottom: `1px solid ${v2.lineSoft}`,
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  "&:hover": { background: v2.canvas },
                }}
              >
                {/*
                  The thumbnail's column, now carrying the verification mark instead — the same
                  leading column, the same two-line span, and the same arrangement as a food row
                  inside a meal, so a food looks the same before and after it is added. The image
                  it replaces was a placeholder fork on most foods, which said nothing; this says
                  whether anybody checked the figures.
                */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Box sx={{ display: "grid", placeItems: "center", width: "20px", flex: "none" }}>
                    {candidate.verified && (
                      <VerifiedIcon sx={{ fontSize: 18, color: "#4CC38A" }} aria-label="מזון מאומת" />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                      <Box sx={{ fontSize: "12.5px", fontWeight: 600, color: v2.ink }}>
                        {candidate.name}
                        {candidate.brand && (
                          <Box component="span" sx={{ color: v2.ink3, fontWeight: 500 }}> · {candidate.brand}</Box>
                        )}
                      </Box>
                      {candidate.isCompanyOwn && (
                        <Box
                          sx={{
                            fontSize: "9.5px",
                            fontWeight: 700,
                            padding: "1px 7px",
                            borderRadius: "20px",
                            background: "#F7F5FE",
                            color: "#5B4BA8",
                            whiteSpace: "nowrap",
                          }}
                        >
                          של החברה
                        </Box>
                      )}
                    </Box>
                    <MacroLine size={10.5} totals={candidatePreview(candidate)} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Popper>
      </Box>

      <TextButton onClick={() => setCustomOpen(true)} sx={{ height: "36px" }}>
        <FreeIcon sx={{ fontSize: 15 }} />
        מזון שאינו במאגר
      </TextButton>

      <CustomFoodDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        onAdd={(item) => {
          onAdd(item);
          setCustomOpen(false);
        }}
      />

    </Box>
  );
};

export default FoodSearchField;
